import { z } from 'zod';

// Enums matching database
export const BrokerageCoverageType = z.enum(['national', 'regional', 'local']);
export type BrokerageCoverageType = z.infer<typeof BrokerageCoverageType>;

export const BrokerageType = z.enum(['physical', 'hybrid', 'virtual']);
export type BrokerageType = z.infer<typeof BrokerageType>;

export const BrokerageLeadStatus = z.enum(['new', 'contacted', 'hired', 'not_interested']);
export type BrokerageLeadStatus = z.infer<typeof BrokerageLeadStatus>;

export const BrokerageSize = z.enum(['small', 'medium', 'large']);
export type BrokerageSize = z.infer<typeof BrokerageSize>;

export const SupportLevel = z.enum(['high', 'medium', 'low']);
export type SupportLevel = z.infer<typeof SupportLevel>;

// Best For options
export const BestForOption = z.enum([
  'new_agents',
  'growing_agents', 
  'top_producers',
  'team_leaders',
  'part_time_agents'
]);
export type BestForOption = z.infer<typeof BestForOption>;

// What Matters / Priorities options
export const PriorityOption = z.enum([
  'mentorship',
  'training',
  'splits',
  'leads',
  'fees',
  'flexibility',
  'tech',
  'brand_recognition',
  'culture'
]);
export type PriorityOption = z.infer<typeof PriorityOption>;

// Cap period options
export const CapPeriod = z.enum(['annual', 'rolling']);
export type CapPeriod = z.infer<typeof CapPeriod>;

// Transaction fee type
export const TransactionFeeType = z.enum(['flat', 'percent']);
export type TransactionFeeType = z.infer<typeof TransactionFeeType>;

// Testimonial video schema (matching products)
export const ZTestimonialVideo = z.object({
  video_url: z.string(),
  thumbnail_url: z.string().optional(),
  author: z.string(),
  company: z.string(),
  quote: z.string(),
});
export type TestimonialVideo = z.infer<typeof ZTestimonialVideo>;

// Feature section schema (matching products)
export const ZFeatureSection = z.object({
  headline: z.string(),
  bullets: z.array(z.string()),
  imageUrl: z.string().optional(),
});
export type FeatureSection = z.infer<typeof ZFeatureSection>;

// Full brokerage schema
export const ZBrokerage = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  logo: z.string().nullable(),
  description: z.string().nullable(),
  
  // Media fields (matching products)
  image: z.string().nullable().optional(),
  subtitle: z.string().nullable().optional(),
  profile_image: z.string().nullable().optional(),
  gallery: z.array(z.string()).default([]),
  demo_video_url: z.string().nullable().optional(),
  podcast_audio_url: z.string().nullable().optional(),
  podcast_title: z.string().nullable().optional(),
  testimonial_videos: z.array(ZTestimonialVideo).default([]),
  feature_sections: z.array(ZFeatureSection).default([]),
  show_feature_sections: z.boolean().default(true),
  show_testimonials: z.boolean().default(true),
  
  // Coverage
  coverage_type: BrokerageCoverageType.nullable(),
  operating_states: z.array(z.string()).default([]),
  
  // Classification
  brokerage_size: z.string().nullable(),
  brokerage_type: BrokerageType.nullable(),
  support_level: z.string().nullable(),
  
  // Filters/Tags
  best_for: z.array(z.string()).default([]),
  priorities: z.array(z.string()).default([]),
  
  // Comparison fields
  training_availability: z.string().nullable(),
  mentorship_structure: z.string().nullable(),
  compensation_model: z.string().nullable(),
  tech_provided: z.string().nullable(),
  lead_programs: z.string().nullable(),
  
  // Culture axes
  culture_independent_vs_collaborative: z.string().nullable(),
  culture_traditional_vs_innovative: z.string().nullable(),
  
  // Recruiting pitch
  what_makes_us_different: z.string().nullable(),
  ideal_agent_description: z.string().nullable(),
  
  // Website
  website_url: z.string().nullable(),
  
  // ========== FINANCIAL / COMPENSATION FIELDS ==========
  
  // Commission Structure
  split_starting_percent: z.number().nullable(), // e.g., 70.00 for 70%
  split_max_percent: z.number().nullable(),      // e.g., 85.00 for top producers
  cap_amount: z.number().nullable(),             // Annual cap amount (null = no cap)
  cap_period: z.string().nullable(),             // 'annual' | 'rolling'
  
  // Recurring Fees  
  monthly_fee: z.number().nullable(),            // Desk fee / monthly fee
  desk_fee_optional: z.boolean().default(false),
  technology_fee: z.number().nullable(),
  eo_fee: z.number().nullable(),                 // E&O insurance
  marketing_fee: z.number().nullable(),
  royalty_fee_percent: z.number().nullable(),    // Franchise royalty
  
  // Transaction Fees
  transaction_fee: z.number().nullable(),        // Per-transaction fee
  transaction_fee_type: z.string().nullable(),   // 'flat' | 'percent'
  
  // Sign-on & Bonuses
  sign_on_bonus_min: z.number().nullable(),
  sign_on_bonus_max: z.number().nullable(),
  sign_on_bonus_notes: z.string().nullable(),
  
  // Revenue Share / Profit Share
  has_revenue_share: z.boolean().default(false),
  revenue_share_description: z.string().nullable(),
  has_stock_awards: z.boolean().default(false),
  stock_awards_description: z.string().nullable(),
  
  // Fee Notes (for exceptions/conditions)
  fee_notes: z.string().nullable(),
  
  // ========== END FINANCIAL FIELDS ==========
  
  // Admin
  sort_order: z.number().default(0),
  is_published: z.boolean().default(false),
  is_claimed: z.boolean().default(false),
  claimed_by: z.string().uuid().nullable(),
  claimed_at: z.string().nullable(),
  
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
});

export type Brokerage = z.infer<typeof ZBrokerage>;

// Brokerage location schema
export const ZBrokerageLocation = z.object({
  id: z.string().uuid(),
  brokerage_id: z.string().uuid(),
  
  name: z.string(),
  city: z.string(),
  state: z.string(),
  address: z.string().nullable(),
  zip_code: z.string().nullable(),
  
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  
  is_virtual: z.boolean().default(false),
  
  recruiter_name: z.string().nullable(),
  recruiter_email: z.string().nullable(),
  recruiter_phone: z.string().nullable(),
  
  booking_url: z.string().nullable(),
  location_pitch: z.string().nullable(),
  
  is_active: z.boolean().default(true),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
  
  // Computed field for distance (added by queries)
  distance_miles: z.number().optional(),
});

export type BrokerageLocation = z.infer<typeof ZBrokerageLocation>;

// Brokerage lead schema
export const ZBrokerageLead = z.object({
  id: z.string().uuid().optional(),
  brokerage_id: z.string().uuid(),
  location_id: z.string().uuid().nullable().optional(),
  
  agent_name: z.string().min(1, 'Name is required'),
  agent_email: z.string().email('Valid email is required'),
  agent_phone: z.string().optional(),
  message: z.string().optional(),
  
  user_id: z.string().uuid().nullable().optional(),
  agent_city: z.string().optional(),
  agent_state: z.string().optional(),
  
  status: BrokerageLeadStatus.default('new'),
  source: z.string().default('website'),
  
  created_at: z.string().optional(),
});

export type BrokerageLead = z.infer<typeof ZBrokerageLead>;

// Lead form input schema (for form validation)
export const ZBrokerageLeadInput = z.object({
  brokerage_id: z.string().uuid(),
  location_id: z.string().uuid().nullable().optional(),
  agent_name: z.string().min(1, 'Name is required'),
  agent_email: z.string().email('Valid email is required'),
  agent_phone: z.string().optional(),
  message: z.string().optional(),
  agent_city: z.string().optional(),
  agent_state: z.string().optional(),
});

export type BrokerageLeadInput = z.infer<typeof ZBrokerageLeadInput>;

// Card display subset (enhanced with financial snapshot)
export const ZBrokerageCard = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  logo: z.string().nullable(),
  description: z.string().nullable(),
  coverage_type: BrokerageCoverageType.nullable(),
  brokerage_size: z.string().nullable(),
  brokerage_type: BrokerageType.nullable(),
  support_level: z.string().nullable(),
  best_for: z.array(z.string()).default([]),
  is_published: z.boolean(),
  
  // Financial snapshot for cards
  split_starting_percent: z.number().nullable().optional(),
  split_max_percent: z.number().nullable().optional(),
  cap_amount: z.number().nullable().optional(),
  monthly_fee: z.number().nullable().optional(),
  transaction_fee: z.number().nullable().optional(),
  sign_on_bonus_max: z.number().nullable().optional(),
  has_revenue_share: z.boolean().optional(),
  has_stock_awards: z.boolean().optional(),
  
  // Computed: number of locations in user's state
  locations_in_state: z.number().optional(),
});

export type BrokerageCard = z.infer<typeof ZBrokerageCard>;

// Filter parameters
export const ZBrokerageFilters = z.object({
  state: z.string().optional(),
  city: z.string().optional(),
  lat: z.number().optional(),
  lng: z.number().optional(),
  radius_miles: z.number().default(50),
  
  brokerage_size: z.array(z.string()).optional(),
  brokerage_type: z.array(z.string()).optional(),
  support_level: z.array(z.string()).optional(),
  best_for: z.array(z.string()).optional(),
  priorities: z.array(z.string()).optional(),
  
  search: z.string().optional(),
  
  // Pagination
  limit: z.number().default(24),
  offset: z.number().default(0),
});

export type BrokerageFilters = z.infer<typeof ZBrokerageFilters>;

// Agent location state
export const ZAgentLocation = z.object({
  state: z.string(),
  city: z.string().optional(),
  lat: z.number().optional(),
  lng: z.number().optional(),
  radius_miles: z.number().default(50),
  source: z.enum(['manual', 'geolocation', 'ip']).default('manual'),
});

export type AgentLocation = z.infer<typeof ZAgentLocation>;

// Calculator input type
export interface CommissionCalculatorInput {
  annualSalesVolume: number;
  numberOfTransactions: number;
  avgCommissionPercent: number;
}

// Calculator result type
export interface CommissionCalculatorResult {
  grossCommission: number;
  brokerSplit: number;
  monthlyFees: number;
  transactionFees: number;
  otherFees: number;
  capApplied: boolean;
  capSavings: number;
  netIncome: number;
}

// Filter option labels
export const BEST_FOR_LABELS: Record<string, string> = {
  new_agents: 'New Agents',
  growing_agents: 'Growing Agents',
  top_producers: 'Top Producers',
  team_leaders: 'Team Leaders',
  part_time_agents: 'Part-Time Agents',
};

export const PRIORITIES_LABELS: Record<string, string> = {
  mentorship: 'Mentorship',
  training: 'Training Programs',
  splits: 'Commission Splits',
  leads: 'Lead Generation',
  fees: 'Low Fees',
  flexibility: 'Flexibility',
  tech: 'Technology',
  brand_recognition: 'Brand Recognition',
  culture: 'Culture & Community',
};

// Alias for backwards compatibility
export const PRIORITY_LABELS = PRIORITIES_LABELS;

export const SUPPORT_LEVEL_LABELS: Record<string, string> = {
  high: 'High Support',
  medium: 'Medium Support',
  low: 'Low Support (Independent)',
};

export const BROKERAGE_SIZE_LABELS: Record<string, string> = {
  small: 'Boutique / Small',
  medium: 'Medium',
  large: 'Large / National',
};

export const BROKERAGE_TYPE_LABELS: Record<string, string> = {
  physical: 'Physical Office',
  hybrid: 'Hybrid',
  virtual: 'Virtual / Cloud',
};

export const COVERAGE_TYPE_LABELS: Record<string, string> = {
  national: 'National',
  regional: 'Regional',
  local: 'Local',
};

// Filter options for UI
export const BEST_FOR_OPTIONS = Object.entries(BEST_FOR_LABELS).map(([value, label]) => ({ value, label }));
export const BROKERAGE_SIZE_OPTIONS = Object.entries(BROKERAGE_SIZE_LABELS).map(([value, label]) => ({ value, label }));
export const BROKERAGE_TYPE_OPTIONS = Object.entries(BROKERAGE_TYPE_LABELS).map(([value, label]) => ({ value, label }));
export const PRIORITIES_OPTIONS = Object.entries(PRIORITIES_LABELS).map(([value, label]) => ({ value, label }));

// US States for location selector
export const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'Washington D.C.' },
];

/**
 * Calculate net income for an agent at a brokerage
 */
export function calculateCommission(
  brokerage: Brokerage,
  input: CommissionCalculatorInput
): CommissionCalculatorResult {
  const { annualSalesVolume, numberOfTransactions, avgCommissionPercent } = input;
  
  // Gross commission from sales
  const grossCommission = annualSalesVolume * (avgCommissionPercent / 100);
  
  // Calculate broker split
  const splitPercent = brokerage.split_starting_percent || 70;
  const agentSharePercent = splitPercent / 100;
  const brokerSharePercent = 1 - agentSharePercent;
  
  let brokerSplit = grossCommission * brokerSharePercent;
  let capApplied = false;
  let capSavings = 0;
  
  // Apply cap if exists
  if (brokerage.cap_amount && brokerSplit > brokerage.cap_amount) {
    capSavings = brokerSplit - brokerage.cap_amount;
    brokerSplit = brokerage.cap_amount;
    capApplied = true;
  }
  
  // Monthly fees (annual total)
  const monthlyFee = brokerage.monthly_fee || 0;
  const techFee = brokerage.technology_fee || 0;
  const eoFee = brokerage.eo_fee || 0;
  const marketingFee = brokerage.marketing_fee || 0;
  const monthlyFees = (monthlyFee + techFee + eoFee + marketingFee) * 12;
  
  // Transaction fees
  let transactionFees = 0;
  if (brokerage.transaction_fee) {
    if (brokerage.transaction_fee_type === 'percent') {
      transactionFees = grossCommission * (brokerage.transaction_fee / 100);
    } else {
      transactionFees = brokerage.transaction_fee * numberOfTransactions;
    }
  }
  
  // Royalty fees (franchise)
  let otherFees = 0;
  if (brokerage.royalty_fee_percent) {
    otherFees = grossCommission * (brokerage.royalty_fee_percent / 100);
  }
  
  // Net income
  const netIncome = grossCommission - brokerSplit - monthlyFees - transactionFees - otherFees;
  
  return {
    grossCommission,
    brokerSplit,
    monthlyFees,
    transactionFees,
    otherFees,
    capApplied,
    capSavings,
    netIncome,
  };
}

/**
 * Format currency for display
 */
export function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format split percentage for display
 */
export function formatSplit(min: number | null | undefined, max: number | null | undefined): string {
  if (!min && !max) return '—';
  if (min && max && min !== max) {
    return `${min}% - ${max}%`;
  }
  return `${min || max}%`;
}
